import { CloseOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Progress, Table, TableColumnsType, TableProps, Tooltip, Typography } from 'antd'
import { keyBy, omit, round, toNumber } from 'lodash'
import moment from 'moment'
import React, { useRef } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { MosaicComponent, ProjectProgress, RelationsDeleter, getPointPermission, statusOptions } from '@/components'
import { DepartmentName, RiskBar, UserAvatar } from '@/components'
import { getProjectDelay } from '@/components/resource-evaluation'
import { routes } from '@/router'
import {
  PAGE_RESOURCE_POINTS_ENUM,
  PpRelation,
  PriorityEnum,
  ProjectStage,
  ProjectStatus,
  ResourceType,
} from '@/schemas'
import { traceEvent } from '@/utils/ga'

interface PortfolioListProps extends TableProps<PpRelation> {
  selfId: string
  type:
    | ResourceType.PROJECT
    | ResourceType.PORTFOLIO
    | ResourceType.OBJECTIVE
    | ResourceType.PRODUCTION
    | ResourceType.DEVLOP
    | ResourceType.CUSTOMER
  onSuccess: () => void
  canUpdate: boolean
  canDelete: boolean
  renderExtras?: any
  defaultFilter?: number[]
  pageType: ResourceType
}

export const calProjectStageStatus = function (stages: ProjectStage[]) {
  return (stages || []).map((item, index) => {
    let status = 0
    const preItem = stages[index - 1]
    if (preItem && !preItem.actualEndDate) {
      status = 0
    }
    if (item.planEndDate) {
      if (item.actualEndDate) {
        status = item.actualEndDate <= item.planEndDate ? 2 : 3
      } else {
        status = moment().format('YYYY-MM-DD') <= item.planEndDate ? 1 : 3
      }
    }
    return {
      ...item,
      status,
    }
  })
}

export const deleteChildren = (data: PpRelation[]): PpRelation[] => {
  return data.map((d) =>
    d.children.length === 0
      ? omit(d, 'children')
      : {
          ...d,
          children: deleteChildren(d.children),
        }
  ) as any
}

const reorder = (res: PpRelation[], sourceIndex: number, targetIndex: number) => {
  // const res = Array.from(list)
  const [item] = res.splice(sourceIndex, 1)
  res.splice(targetIndex, 0, item)
  return res.map((r, index) => ({
    id: r.id,
    order: index,
  }))
}

const flatten = (data: any) => {
  let arr = new Array<any>()
  for (let i = 0; i < data.length; i++) {
    if (data[i].children && data[i].children.length) {
      arr = arr.concat(flatten(data[i].children))
    }
    arr.push(data[i])
  }
  return arr
}

export const NormalResourceTable: React.FC<PortfolioListProps> = ({
  selfId,
  canUpdate,
  canDelete,
  onSuccess,
  renderExtras,
  defaultFilter = [],
  pageType,
  ...extraTableProps
}) => {
  const { t } = useTranslation()
  const columns: TableColumnsType<PpRelation> = [
    {
      title: t('owner'),
      width: 200,
      render: (_, d) => {
        const item = {
          [ResourceType.PROJECT]: d.project,
          [ResourceType.PORTFOLIO]: d.portfolio,
          [ResourceType.CUSTOMER]: d.customer,
          [ResourceType.PRODUCTION]: d.production,
          [ResourceType.DEVLOP]: d.devlop,
        }[d.type]
        return (
          <div className='flex items-stretch'>
            <RiskBar risk={item.risk} />
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, item.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <UserAvatar showName email={item.email} />
            )}
          </div>
        )
      },
    },
    {
      title: t('projects/portfolios.name'),
      render: (_, d) => {
        const item = {
          [ResourceType.PROJECT]: d.project,
          [ResourceType.PORTFOLIO]: d.portfolio,
          [ResourceType.CUSTOMER]: d.customer,
          [ResourceType.PRODUCTION]: d.production,
          [ResourceType.DEVLOP]: d.devlop,
        }[d.type]
        const path = {
          [ResourceType.PROJECT]: routes.project.page(item.selfId),
          [ResourceType.PORTFOLIO]: routes.portfolio.page(item.selfId),
          [ResourceType.CUSTOMER]: routes.customer.detail(item.selfId),
          [ResourceType.PRODUCTION]: routes.production.detail(item.selfId),
          [ResourceType.DEVLOP]: routes.devlop.detail(item.selfId),
        }[d.type]
        return (
          <div className='flex items-center'>
            <div>
              <div>
                <Link
                  to={path}
                  onClick={() => {
                    let entry
                    switch (pageType) {
                      case ResourceType.PORTFOLIO:
                        entry = traceEvent.detailPage.event_categories.ef_pmo_portfolios_detail
                        break
                      case ResourceType.TEAM:
                        entry = traceEvent.detailPage.event_categories.ef_pmo_teams
                        break
                      default:
                        entry = 'unknown'
                    }
                    window.sessionStorage.setItem('entry', entry)
                  }}
                >
                  {item.type !== ResourceType.CUSTOMER ? (
                    item.name
                  ) : getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, item.permissions).canRead ? (
                    item.name
                  ) : (
                    <MosaicComponent content={item.name} />
                  )}
                </Link>
              </div>
              <Typography.Text type='secondary'>
                <>
                  <DepartmentName
                    canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_所属部门, item.permissions).canRead}
                    departmentId={item.departmentId}
                  />
                </>
              </Typography.Text>
            </div>
            {canDelete && (
              <RelationsDeleter
                selfId={item.selfId}
                type={item.type}
                parentSelfId={d.parentSelfId}
                parentType={d.parentType}
                onSuccess={onSuccess}
              >
                <Button
                  className='ml-12px opacity-0 group-hover:opacity-100'
                  danger
                  size='small'
                  shape='circle'
                  icon={<CloseOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </RelationsDeleter>
            )}
          </div>
        )
      },
    },
    {
      title: t('status'),
      width: 130,
      filters: statusOptions.map((s) => {
        return {
          text: t(s.label),
          value: s.value,
        }
      }),
      defaultFilteredValue: statusOptions
        .filter((s) => (defaultFilter.length ? !defaultFilter.includes(s.value) : true))
        .map((s) => `${s.value}`),
      onFilter: (value, d) => (d.project ? d.project.status === value : true),
      render: (_, d) => {
        if (d.type === ResourceType.PROJECT) {
          const currentStage = calProjectStageStatus(d.project.projectStages).find((s) => s.status === 1)
          return (
            <div>
              {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, d.project.permissions).canRead ? (
                <MosaicComponent />
              ) : (
                <>
                  <span>{d.project.status ? t(ProjectStatus[d.project.status]) : null}</span>
                  {currentStage && (
                    <div>
                      {t('project.stages')}: {currentStage.name}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        }
        if (d.type === ResourceType.PORTFOLIO) {
          return null
        }
        return null
      },
    },
    {
      title: t('delay'),
      width: 70,
      align: 'center',
      render: (_, d) => {
        if (d.type === ResourceType.PROJECT) {
          if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.阶段与任务, d.project.permissions).canRead) {
            return <MosaicComponent />
          }

          const projectStages = d.project.projectStages || []
          const delayDays = getProjectDelay(projectStages).delayDays
          return (
            <Tooltip title={t('delay')}>
              <Typography.Text type={delayDays > 0 ? 'danger' : undefined}>
                {projectStages.length === 0 ? t('stage.nodata') : delayDays + ' ' + t('days')}
              </Typography.Text>
            </Tooltip>
          )
        }
        return null
      },
    },
    {
      title: t('progress'),
      width: 60,
      responsive: ['lg'],
      align: 'center',
      render: (_, d) => {
        if (d.type === ResourceType.PROJECT) {
          return <ProjectProgress project={d.project} />
        }
        if (d.type === ResourceType.PORTFOLIO) {
          return getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_进度, d.portfolio.permissions).canRead ? (
            <Progress
              className='font-mono flex-shrink-0'
              type='circle'
              width={26}
              percent={round(d.portfolio.progress, 0)}
            />
          ) : (
            <MosaicComponent />
          )
        }
        return null
      },
    },
    {
      title: t('priority'),
      width: 80,
      align: 'center',
      filters: Object.keys(PriorityEnum)
        .filter((v) => isNaN(toNumber(v)))
        .map((key) => ({
          text: t(`form.priority.${key}`),
          value: PriorityEnum[key],
        })),
      onFilter: (value, d) => {
        if (d.type === ResourceType.PROJECT) {
          return d.project.priority === value
        }
        if (d.type === ResourceType.PORTFOLIO) {
          return d.portfolio.priority === value
        }
        return false
      },
      render: (_, d) => {
        if (
          d.type === ResourceType.PROJECT &&
          !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, d.project.permissions).canRead
        ) {
          return <MosaicComponent />
        }

        if (
          d.type === ResourceType.PORTFOLIO &&
          !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, d.portfolio.permissions).canRead
        ) {
          return <MosaicComponent />
        }

        if (d.type === ResourceType.PROJECT && d.project.priority) {
          return t(`form.priority.${PriorityEnum[d.project.priority]}`)
        }
        if (d.type === ResourceType.PORTFOLIO && d.portfolio.priority) {
          return t(`form.priority.${PriorityEnum[d.portfolio.priority]}`)
        }
        return null
      },
    },
    {
      title: t('startend'),
      width: 200,
      align: 'center',
      render: (_, d) => {
        const p = {
          [ResourceType.PROJECT]: d.project,
          [ResourceType.PORTFOLIO]: d.portfolio,
          [ResourceType.CUSTOMER]: d.customer,
          [ResourceType.PRODUCTION]: d.production,
          [ResourceType.DEVLOP]: d.devlop,
        }[d.type]
        return (
          <div className='flex text-sm'>
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, p.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <>
                <div className='w-80px'>{p.startDate && moment(p.startDate).format('l')}</div>
                <div className='px-4px'>-</div>
                <div className='w-80px'>{p.endDate && moment(p.endDate).format('l')}</div>
              </>
            )}
          </div>
        )
      },
    },
  ]

  if (renderExtras) {
    columns.push({
      width: 80,
      align: 'center',
      render: renderExtras,
    })
  }

  const { run: updateOrder } = useRequest(
    ({ children }) => ({
      url: '/api/project/updatePpRelationsOrder',
      method: 'post',
      data: {
        selfId,
        children,
      },
    }),
    {
      manual: true,
      onSuccess: onSuccess,
    }
  )

  const data = Array.from(extraTableProps.dataSource as PpRelation[])
  const dataFlatMap = keyBy(flatten(data), 'selfId')

  // TODO 本地存在BUG 后续需要调查
  const DraggableBodyRow = ({ index, record, className, style, ...restProps }: any) => {
    if (!record) return null
    const ref = useRef()

    const [{ isOver, dropClassName }, drop] = useDrop({
      accept: record.parentSelfId,
      collect: (monitor: any) => {
        const item = monitor.getItem()
        if (!item) return {}
        const { index: dragIndex } = monitor.getItem() || {}
        if (dragIndex === index) {
          return {}
        }
        return {
          isOver: monitor.isOver(),
          dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
        }
      },
      drop: async (item: any) => {
        if (item.index === index) return
        if (!dataFlatMap) return
        let sendOrder = []
        if (item.parent === selfId) {
          sendOrder = reorder(data, item.index, index)
        } else {
          sendOrder = reorder(dataFlatMap[item.parent].children, item.index, index)
        }

        await updateOrder({ children: sendOrder })
      },
    })

    const [, drag] = useDrag({
      type: record.parentSelfId,
      canDrag: canUpdate,
      item: { index, parent: record.parentSelfId },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    })

    drop(drag(ref))

    return (
      <tr
        ref={ref}
        className={`${className}${isOver ? dropClassName : ''}`}
        style={{ cursor: canUpdate ? 'pointer' : 'auto', ...style }}
        {...restProps}
      />
    )
  }
  return (
    <DndProvider backend={HTML5Backend}>
      <Table
        rowClassName='group'
        rowKey={(record) => `${record.selfId}-${record.parentSelfId}`}
        size='middle'
        columns={columns}
        components={{
          body: {
            row: DraggableBodyRow,
          },
        }}
        {...extraTableProps}
        pagination={false}
        onRow={(record, index) => ({
          index,
          record,
          onError: () => {},
        })}
      />
    </DndProvider>
  )
}
